import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import axios from 'axios';
import { X } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const GuideRequestContext = createContext({ open: () => {} });

export const useGuideRequest = () => useContext(GuideRequestContext);

const emailValid = (s) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test((s || '').trim());

export const GuideRequestProvider = ({ children }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [source, setSource] = useState('unknown');
  const [email, setEmail] = useState('');
  const [website, setWebsite] = useState(''); // honeypot
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const [emailError, setEmailError] = useState('');
  const [done, setDone] = useState(false);

  const open = useCallback((src) => {
    setSource(src || 'unknown');
    setEmail(''); setWebsite(''); setEmailError(''); setError(''); setDone(false); setSending(false);
    setIsOpen(true);
  }, []);

  const close = useCallback(() => setIsOpen(false), []);

  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e) => { if (e.key === 'Escape') close(); };
    document.addEventListener('keydown', onKey);
    // lock scroll
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
    };
  }, [isOpen, close]);

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    if (!emailValid(email)) {
      setEmailError('Enter a valid email.');
      return;
    }
    setEmailError('');
    setSending(true);
    try {
      await axios.post(`${API}/guide-request`, {
        email: email.trim(),
        source,
        website,
      });
      setDone(true);
    } catch (err) {
      const status = err?.response?.status;
      if (status === 429) {
        setError('Too many requests. Please try again later.');
      } else {
        setError('Something went wrong. Try again, or email dataroom@starteaseai.com.');
      }
    } finally {
      setSending(false);
    }
  };

  return (
    <GuideRequestContext.Provider value={{ open }}>
      {children}
      {isOpen && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center px-4"
          role="dialog"
          aria-modal="true"
          aria-labelledby="guide-modal-heading"
          data-testid="guide-modal"
        >
          {/* Backdrop */}
          <button
            type="button"
            aria-label="Close"
            onClick={close}
            className="absolute inset-0 bg-black/70 backdrop-blur-sm"
            data-testid="guide-modal-backdrop"
          />
          {/* Card */}
          <div
            className="relative w-full max-w-md rounded-xl border border-white/10 bg-[#111827] shadow-2xl p-7 sm:p-8"
            data-testid="guide-modal-card"
          >
            <button
              type="button"
              onClick={close}
              className="absolute top-3 right-3 p-2 text-slate-400 hover:text-white transition-colors"
              aria-label="Close modal"
              data-testid="guide-modal-close"
            >
              <X className="w-4 h-4" />
            </button>

            {!done ? (
              <>
                <h2
                  id="guide-modal-heading"
                  className="font-serif text-[22px] sm:text-2xl font-semibold text-white leading-tight mb-3"
                  data-testid="guide-modal-heading"
                >
                  Which US laws apply to your AI product?
                </h2>
                <p className="text-[14.5px] text-slate-400 leading-[1.6] mb-6">
                  Enter your email and the free guide lands in your inbox: 8 domains, the laws
                  on each, and the documents they demand.
                </p>

                <form onSubmit={submit} noValidate>
                  {/* Honeypot — visually hidden but present */}
                  <div
                    aria-hidden="true"
                    style={{
                      position: 'absolute',
                      left: '-9999px',
                      width: '1px',
                      height: '1px',
                      overflow: 'hidden',
                    }}
                  >
                    <label htmlFor="gm-website">Website (leave blank)</label>
                    <input
                      id="gm-website"
                      name="website"
                      type="text"
                      tabIndex={-1}
                      autoComplete="off"
                      value={website}
                      onChange={(e) => setWebsite(e.target.value)}
                    />
                  </div>

                  <label htmlFor="gm-email" className="sr-only">Your email</label>
                  <input
                    id="gm-email"
                    type="email"
                    inputMode="email"
                    autoComplete="email"
                    placeholder="you@company.com"
                    value={email}
                    onChange={(e) => { setEmail(e.target.value); if (emailError) setEmailError(''); }}
                    disabled={sending}
                    aria-invalid={!!emailError}
                    aria-describedby={emailError ? 'gm-email-err' : undefined}
                    className={`w-full rounded-lg bg-[#0b1310] text-white placeholder:text-slate-500 px-4 py-3 text-[15px] focus:outline-none focus:ring-2 focus:ring-[#059669]/40 border ${emailError ? 'border-red-500/70' : 'border-white/10 focus:border-[#059669]/60'}`}
                    data-testid="guide-modal-email"
                  />
                  {emailError && (
                    <p id="gm-email-err" className="text-red-400 text-[13px] mt-1.5" data-testid="guide-modal-email-error">
                      {emailError}
                    </p>
                  )}

                  <button
                    type="submit"
                    disabled={sending}
                    className="w-full mt-4 inline-flex items-center justify-center gap-2 bg-[#059669] hover:bg-[#047857] disabled:opacity-60 disabled:cursor-not-allowed text-white font-semibold text-[15px] px-6 py-3.5 rounded-lg transition-colors"
                    data-testid="guide-modal-submit"
                  >
                    {sending ? 'Sending...' : 'Send Me the Free Guide'}
                  </button>

                  {error && (
                    <p className="text-red-400 text-[13px] mt-3 leading-relaxed" data-testid="guide-modal-error">
                      {error}
                    </p>
                  )}
                </form>
              </>
            ) : (
              <div data-testid="guide-modal-success">
                <h2 className="font-serif text-[22px] sm:text-2xl font-semibold text-white leading-tight mb-3">
                  Sent.
                </h2>
                <p className="text-[14.5px] text-slate-400 leading-[1.65]">
                  Check your inbox (and spam, once). Reply to that email with what you&apos;re
                  building and we&apos;ll name your exact laws free.
                </p>
                <button
                  type="button"
                  onClick={close}
                  className="mt-6 inline-flex items-center gap-2 border border-white/10 hover:border-[#059669]/60 text-white font-semibold text-[15px] px-6 py-3 rounded-lg transition-colors"
                  data-testid="guide-modal-done"
                >
                  Close
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </GuideRequestContext.Provider>
  );
};

export default GuideRequestProvider;
